import { ReactNode } from 'react';
import { cx } from './cx';
import { typography } from './Text';

export type BadgeColor = 'gray' | 'brand' | 'success' | 'warning' | 'error';
export type BadgeSize = 'sm' | 'md';

/** Per-color classes — literal strings so Tailwind's scanner keeps them. */
const colorClasses: Record<BadgeColor, string> = {
  gray: 'bg-muted-strong text-fg-secondary border-default',
  brand: 'bg-brand-solid/12 text-brand-text border-brand-solid/30',
  success: 'bg-success-500/12 text-success border-success-500/25',
  warning: 'bg-warning-500/12 text-warning border-warning-500/25',
  error: 'bg-error-500/12 text-error border-error-500/25',
};

const dotClasses: Record<BadgeColor, string> = {
  gray: 'bg-fg-muted',
  brand: 'bg-brand-solid',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-error',
};

export interface BadgeProps {
  color?: BadgeColor;
  size?: BadgeSize;
  /** Leading status dot in the badge's color. */
  dot?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Status / label badge — the Untitled UI pill. Colors map to foundation
 * semantics (gray = neutral, brand, success, warning, error); an optional
 * leading dot reinforces status. Used for lifecycle, type, run status, etc.
 */
export const Badge = ({
  color = 'gray',
  size = 'sm',
  dot = false,
  className,
  children,
}: BadgeProps) => (
  <span
    className={cx(
      typography.caption,
      'inline-flex items-center gap-1.5 rounded-full border font-medium whitespace-nowrap',
      size === 'sm' ? 'px-2 py-0.5' : 'px-2.5 py-1',
      colorClasses[color],
      className,
    )}
  >
    {dot && (
      <span
        className={cx('size-1.5 shrink-0 rounded-full', dotClasses[color])}
      />
    )}
    {children}
  </span>
);
